
// useExportToExcel.js
import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DASH_PLACEHOLDER, formatMissingValue } from './missingValue';

export type ExcelExportColumn = {
  field: string;
  headerName?: string;
};

const getFirstAvailableValue = (obj: any, keys: string[], fallback: any = '') => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return fallback;
};

const SPLOT_ID_EXPORT_KEYS = ['splot_id', 'sp_lot_id'];

export const DEFAULT_TRIAL_MATERIAL_EXPORT_COLUMNS: ExcelExportColumn[] = [
  { field: 'mat_id', headerName: 'Material ID' },
  { field: 'splot_id', headerName: 'SPLot ID' },
  { field: 'sector', headerName: 'Greenhouse Location' },
  { field: 'admin_code', headerName: 'Admin Code' },
  { field: 'barcode', headerName: 'Barcode' },
  { field: 'pollination_id', headerName: 'Pollination ID' },
  { field: 'pollination_type', headerName: 'Pollination Type' },
  { field: 'plants_to_harvest', headerName: 'Subplot ID' },
  { field: 'hrvmd', headerName: 'HRVMD' },
  { field: 'material_generation', headerName: 'Generation Code' },
  { field: 'material_stage_code', headerName: 'Stage Code' },
  { field: 'processing_priority', headerName: 'Processing Priority' },
  { field: 'special_requisites', headerName: 'Special Requisites' },
  { field: 'type_segment', headerName: 'Type/Segment' },
  { field: 'required_seed_weight_g', headerName: 'Required Seed Weight (g)' },
  { field: 'required_fruit_count', headerName: 'Required Fruit Count' },
  { field: 'required_seed_count', headerName: 'Required Seed Count' },
  { field: 'bags', headerName: 'Bags' },
  { field: 'fruit_weight_g', headerName: 'Fruit Weight (g)' },
  { field: 'fruit_count', headerName: 'Fruit Count' },
  { field: 'seed_count', headerName: 'Seed Count' },
  { field: 'seed_weight_g', headerName: 'Seed Weight (g)' },
  { field: 'goal_achieved_pct', headerName: 'Goal Achieved %' },
  { field: 'remarks', headerName: 'Remarks' },
  { field: 'all_registered_containers', headerName: 'All Registered Containers' },
  { field: 'material_harvests', headerName: 'Harvests' },
  { field: 'current_status', headerName: 'Current Status' },
];

const flattenTrialMaterialRows = (rows: any[]) => {
  return (rows ?? []).flatMap((parent: any) => {
    const childRows = (Array.isArray(parent?._childRows) && parent._childRows.length > 0)
      ? parent._childRows
      : (Array.isArray(parent?.material_harvests)
        ? parent.material_harvests.map((harvest: any, harvestIndex: number) => ({
          ...parent,
          ...harvest,
          _rowType: 'child',
          _parentRowUid: parent?._rowUid,
          _harvestIndex: harvestIndex + 1,
          harvest_name: getFirstAvailableValue(harvest, ['harvest_name', 'name'], `Harvest ${harvestIndex + 1}`),
          harvest_status: getFirstAvailableValue(harvest, ['status', 'harvest_status', 'current_status'], ''),
          splot_id: getFirstAvailableValue(harvest, SPLOT_ID_EXPORT_KEYS, getFirstAvailableValue(parent, SPLOT_ID_EXPORT_KEYS, '')),
          registered_containers: harvest?.registered_containers ?? getFirstAvailableValue(harvest, ['container_id', 'container_alt_id', 'container_uuid']),
          required_grams: getFirstAvailableValue(harvest, ['required_seed_weight_g', 'required_seed_weight', 'required_grams']),
          required_fruit: getFirstAvailableValue(harvest, ['required_fruit_count']),
          required_seed_count: getFirstAvailableValue(harvest, ['required_seed_count']),
          fruit_weight_g: getFirstAvailableValue(harvest, ['fruit_weight', 'fruit_weight_g', 'weight_g']),
          fruit_count: getFirstAvailableValue(harvest, ['fruit_count']),
          seed_count: getFirstAvailableValue(harvest, ['seed_count']),
          seed_weight_g: getFirstAvailableValue(harvest, ['seed_weight_g', 'seed_weight']),
          material_remark: getFirstAvailableValue(harvest, ['material_remark', 'remarks', 'remark_name']),
          remark_id: getFirstAvailableValue(harvest, ['remark_id']),
          remarks: getFirstAvailableValue(harvest, ['remark_id', 'material_remark', 'remarks', 'remark_name']),
        }))
        : []);

    return [parent, ...childRows];
  });
};

const getRemarkText = (row: any, trialRemarks: any[] = []) => {
  const rawRemarkValue = row?.remark_id ?? row?.material_remark ?? row?.remarks ?? '';
  const remarkId = typeof rawRemarkValue === 'number'
    ? rawRemarkValue
    : (trialRemarks.find((r: any) => r.remark_name === rawRemarkValue)?.remark_id ?? rawRemarkValue);

  if (typeof remarkId === 'number') {
    return trialRemarks.find((r: any) => r.remark_id === remarkId)?.remark_name ?? '';
  }

  return typeof rawRemarkValue === 'string' ? rawRemarkValue : '';
};

const getTrialMaterialExportCellValue = (row: any, field: string, trialRemarks: any[] = [], priorities: any[] = []) => {
  const isChild = row?._rowType === 'child';

  if (field === 'mat_id') return row?.mat_id ?? '';
  if (field === 'splot_id') return getFirstAvailableValue(row, SPLOT_ID_EXPORT_KEYS, '');
  if (field === 'harvest_count') return isChild ? (row?.harvest_name ?? '') : (row?.harvest_count ?? 0);
  if (field === 'material_harvests') return isChild ? (row?.harvest_name ?? '') : (Array.isArray(row?.material_harvests) ? row.material_harvests.length : 0);
  if (field === 'all_registered_containers') return isChild ? (row?.registered_containers ?? '') : (row?.all_registered_containers ?? '');
  if (field === 'current_status') return isChild ? (row?.harvest_status ?? '') : (row?.material_status ?? '');
  if (field === 'remarks') return getRemarkText(row, trialRemarks);
  if (field === 'goal_achieved_pct') return isChild ? '' : (row?.goal_achieved_pct ?? 0);
  if (field === 'completion_percentage') return isChild ? '' : `${row?.goal_achieved_pct ?? 0}%`;
  if (field === 'required_seed_weight_g') return row?.required_grams ?? row?.required_seed_weight_g ?? '';
  if (field === 'required_fruit_count') return row?.required_fruit ?? row?.required_fruit_count ?? '';
  if (field === 'bags') return row?.bags ?? row?.bag_number ?? '';
  if (field === 'processing_priority') {
    return priorities.find((p: any) => p.id === row?.processing_priority)?.value ?? (row?.processing_priority ?? '');
  }

  return row?.[field] ?? '';
};

export const buildTrialMaterialsExportData = (
  rows: any[] = [],
  columns: ExcelExportColumn[] = DEFAULT_TRIAL_MATERIAL_EXPORT_COLUMNS,
  options: { trialRemarks?: any[]; priorities?: any[] } = {}
) => {
  const { trialRemarks = [], priorities = [] } = options;
  const exportRows = flattenTrialMaterialRows(rows);

  return exportRows.map((row: any) => {
    const rowData: Record<string, any> = {};
    columns.forEach((column) => {
      const field = String(column.field ?? '');
      const header = String(column.headerName ?? field);
      rowData[header] = getTrialMaterialExportCellValue(row, field, trialRemarks, priorities);
    });
    return rowData;
  });
};


type ExportFileType = 'xlsx' | 'xls' | 'csv';

const MIME_TYPE_BY_FILE: Record<ExportFileType, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv;charset=UTF-8',
};

const normalizeExportValue = (value: any) => {
  if (Array.isArray(value)) {
    return value.length === 0 ? DASH_PLACEHOLDER : value;
  }
  return formatMissingValue(value, DASH_PLACEHOLDER);
};

const reorderAndFilterRow = (row: Record<string, any>, fieldsToRemove: Set<string>, columnOrder: string[]) => {
  const orderedRow: Record<string, any> = {};
  const keys = Object.keys(row);

  const matIdKey = keys.find((k) => k.toLowerCase() === 'matid');
  if (matIdKey && !fieldsToRemove.has(matIdKey)) {
    orderedRow[matIdKey] = row[matIdKey];
  }

  if (columnOrder.length > 0) {
    columnOrder.forEach((field) => {
      if (
        field.toLowerCase() !== 'matid' &&
        Object.prototype.hasOwnProperty.call(row, field) &&
        !fieldsToRemove.has(field)
      ) {
        orderedRow[field] = row[field];
      }
    });
  }

  keys.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(orderedRow, key) && !fieldsToRemove.has(key)) {
      orderedRow[key] = row[key];
    }
  });

  return orderedRow;
};

const sanitizeAndOrderRows = (apiData: any[], fieldToRemove: string[], columnOrder: string[]) => {
  const sourceRows = Array.isArray(apiData) ? apiData : [];
  const fieldsToRemove = new Set(fieldToRemove ?? []);
  return sourceRows.map((row: any) => {
    const ordered = reorderAndFilterRow({ ...(row ?? {}) }, fieldsToRemove, columnOrder);
    const normalized: Record<string, any> = {};
    Object.keys(ordered).forEach((key) => {
      normalized[key] = normalizeExportValue(ordered[key]);
    });
    return normalized;
  });
};

export const normalizeCsvMissingValues = (csvContent: string): string => {
  try {
    const workbook = XLSX.read(csvContent, { type: 'string' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!firstSheet) return csvContent;

    const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: DASH_PLACEHOLDER }) as Record<string, any>[];
    const normalizedRows = rows.map((row) => {
      const normalized: Record<string, any> = {};
      Object.keys(row).forEach((key) => {
        normalized[key] = normalizeExportValue(row[key]);
      });
      return normalized;
    });

    const normalizedSheet = XLSX.utils.json_to_sheet(normalizedRows);
    return XLSX.utils.sheet_to_csv(normalizedSheet);
  } catch {
    return csvContent;
  }
};

export const removeCsvColumns = (csvContent: string, headersToRemove: string[]): string => {
  try {
    const workbook = XLSX.read(csvContent, { type: 'string' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!firstSheet) return csvContent;

    const normalizedHeadersToRemove = new Set(headersToRemove.map((header) => header.trim().toLowerCase()));
    const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' }) as Record<string, any>[];
    const filteredRows = rows.map((row) => {
      const filteredRow: Record<string, any> = {};
      Object.keys(row).forEach((key) => {
        if (!normalizedHeadersToRemove.has(key.trim().toLowerCase())) {
          filteredRow[key] = row[key];
        }
      });
      return filteredRow;
    });

    const filteredSheet = XLSX.utils.json_to_sheet(filteredRows);
    return XLSX.utils.sheet_to_csv(filteredSheet);
  } catch {
    return csvContent;
  }
};

export function useExportToExcel() {
  const exportToExcel = useCallback((apiData: any[], fileName: string, fieldToRemove: string[] = [], columnOrder: string[] = []) => {
    const sheetName = 'Sheet1';
    const fileType: ExportFileType = 'xlsx';
    const includeHeaders = true;

    const copyArr = sanitizeAndOrderRows(apiData, fieldToRemove, columnOrder);

    if (copyArr.length === 0) {
      console.warn('useExportToExcel: apiData is empty');
    }

    try {
      const worksheet = XLSX.utils.json_to_sheet(copyArr, {
        skipHeader: !includeHeaders,
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      const excelBuffer = XLSX.write(workbook, {
        bookType: fileType,
        type: 'array',
      });

      const blob = new Blob([excelBuffer], { type: MIME_TYPE_BY_FILE[fileType] });
      const safeName = (fileName && fileName.trim()) || 'export';
      saveAs(blob, `${safeName}.${fileType}`);
    } catch (err) {
      console.error('useExportToExcel: export failed', err);
    }
  }, []);

  return { exportToExcel };
}
